/**
 * The keyboard's road into the Carry machine (§3.1).
 *
 * F6 moves between regions; inside a region one element is the tab stop and
 * the arrows move it (roving tabindex). Space lifts the focused thing, then
 * the arrows or Tab cycle only the targets that take it, Enter drops, and Esc
 * backs out. Enter on a thing not lifted opens its verb ring. A `role=grid`
 * (the field) moves its own focus in two dimensions; while something is
 * lifted, wherever the grid's focus lands becomes the target.
 *
 * The pure parts (region order, roving index) are exported for tests; the
 * binding is a thin layer over them.
 */

import type { CarryState, Region, Unsub } from '../contracts';
import { isTyping, REGION_ORDER } from '../ui/a11y';
import type { HandInput } from './carry';
import type { Registry } from './targets';

/** The order F6 walks (§3.1), then the screens outside the stage: one list, the shell's, which owns F6 in the app. */
export { REGION_ORDER };

/** The next region with something in it, after `current`, wrapping. */
export const nextRegion = (present: ReadonlySet<Region>, current: Region | null, dir: 1 | -1): Region | null => {
  const order = REGION_ORDER.filter((r) => present.has(r));
  if (order.length === 0) return null;
  const i = current === null ? -1 : order.indexOf(current);
  if (i < 0) return dir > 0 ? order[0]! : order[order.length - 1]!;
  return order[(i + dir + order.length) % order.length]!;
};

/** Where roving focus goes from `index` of `count` on a key; null when the key is not a roving key. */
export const rove = (count: number, index: number, key: string): number | null => {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowRight': case 'ArrowDown': return (index + 1) % count;
    case 'ArrowLeft': case 'ArrowUp': return (index - 1 + count) % count;
    case 'Home': return 0;
    case 'End': return count - 1;
    default: return null;
  }
};

/** What a key does while something is lifted, or a ring is open. Pure, so the carry keys are tested without a DOM. */
export const carryKey = (phase: CarryState['phase'], key: string, shift: boolean, inGrid: boolean): HandInput | 'grid' | 'explain' | 'soft' | 'queue' | null => {
  if (phase === 'ring') {
    if (key === 'Escape') return { type: 'cancel' };
    if (key === 'Enter') return { type: 'key-drop' };
    if (key === 'ArrowDown' || key === 'ArrowRight' || (key === 'Tab' && !shift)) return { type: 'key-cycle', dir: 1 };
    if (key === 'ArrowUp' || key === 'ArrowLeft' || (key === 'Tab' && shift)) return { type: 'key-cycle', dir: -1 };
    if (/^[1-9]$/.test(key)) return { type: 'choose', index: Number(key) - 1 };
    return null;
  }
  if (phase !== 'keyboard' && phase !== 'carrying' && phase !== 'dragging') return null;
  if (key === 'Escape') return { type: 'cancel' };
  if (phase === 'dragging') return null; // the pointer is driving; only Esc (and S/E/Q, in dnd) apply
  if (key === 'Enter') return { type: 'key-drop' };
  if (key === 'Tab') return { type: 'key-cycle', dir: shift ? -1 : 1 };
  if (key.startsWith('Arrow')) {
    if (inGrid) return 'grid';
    return { type: 'key-cycle', dir: key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1 };
  }
  const k = key.toLowerCase();
  if (k === 'e') return 'explain';
  if (k === 's' && !shift) return 'soft';
  if (k === 'q') return 'queue';
  return null;
};

/** What the browser presses (or types into) on Enter by itself. */
const PRESSABLE = new Set(['A', 'BUTTON', 'INPUT', 'SELECT', 'SUMMARY', 'TEXTAREA']);

/**
 * Whether Enter on `el` is the control's own, not the verb ring's: a native control sitting
 * inside a registered source or target (a button on a card over the ground) is pressed, and only
 * the registered element itself (`own`) opens its ring. Pure, so it is tested without a DOM.
 */
export const pressesItself = (el: { readonly tagName?: string } | null, own: unknown): boolean =>
  el !== null && el !== own && PRESSABLE.has(String(el.tagName ?? '').toUpperCase());

const inGrid = (el: Element | null): boolean => !!el?.closest('[role="grid"]');

/**
 * The part of the screen F6 knows an element by (its nearest `data-region`).
 * Roving is kept to it: two areas registered under one hand region (the
 * Inbox's gates and the shell's gate strip, the HUD and the stage) each keep
 * their own tab stop, and the arrows never walk from one into the other.
 */
const zoneOf = (el: Element | null): Element | null =>
  (el && typeof el.closest === 'function' ? el.closest('[data-region]') : null);

export interface KeyboardBinding {
  /** An element joined a region: give it its place in the roving order. */
  adopt(el: HTMLElement, region: Region): void;
  /** Move focus to a region (F6 does this). */
  focusRegion(region: Region): void;
  unbind(): void;
}

export const bindKeyboard = (opts: {
  root: HTMLElement;
  registry: Registry;
  state(): CarryState;
  dispatch(i: HandInput): void;
  announce(text: string): void;
}): KeyboardBinding => {
  const { root, registry } = opts;
  const offs: Unsub[] = [];
  const active = (): Element | null => (typeof document === 'undefined' ? null : document.activeElement);

  /** The things of `region` that sit in the same part of the screen as `el`. */
  const peersOf = (region: Region, el: Element | null): HTMLElement[] => {
    const zone = zoneOf(el);
    return registry.inRegion(region).filter((x) => !inGrid(x) && zoneOf(x) === zone);
  };

  /** The tab stop of `el`'s part of the screen moves to it; the others there step out of the tab order. */
  const settle = (region: Region, el: HTMLElement): void => {
    for (const other of peersOf(region, el)) other.tabIndex = other === el ? 0 : -1;
  };

  const focusRegion = (region: Region): void => {
    const els = registry.inRegion(region);
    const grid = els.find((e) => inGrid(e));
    const stop = grid
      ? (grid.closest('[role="grid"]')?.querySelector('[tabindex="0"]') as HTMLElement | null) ?? grid
      : els.find((e) => e.tabIndex === 0) ?? els[0];
    if (!stop) return;
    stop.focus();
    opts.announce(`${region} region`);
  };

  const onFocus = (e: FocusEvent): void => {
    const el = e.target as HTMLElement | null;
    if (!el || inGrid(el)) return;
    const region = registry.regionOf(el);
    if (region) settle(region, registry.sourceOf(el)?.el ?? registry.targetOf(el)?.el ?? el);
  };

  const mod = (m: 'soft' | 'queue'): void => {
    const on = !opts.state().mods.has(m);
    opts.dispatch({ type: 'mod', mod: m, on });
  };

  const onKey = (e: KeyboardEvent): void => {
    const el = active();
    const phase = opts.state().phase;
    if (phase !== 'idle' && carryKey(phase, e.key, e.shiftKey, inGrid(el)) === 'grid') {
      // The grid has already moved its focus (its handler runs first); what it focused is the new target.
      const target = registry.targetOf(el)?.target() ?? null;
      const rect = (el as HTMLElement | null)?.getBoundingClientRect?.();
      opts.dispatch({ type: 'move', at: rect ? { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 } : { x: 0, y: 0 }, over: target });
      return;
    }
    if (e.defaultPrevented) return;
    if (e.key === 'F6') {
      e.preventDefault();
      const present = new Set<Region>();
      for (const s of registry.sources()) if (s.el.isConnected) present.add(s.region);
      for (const t of registry.targets()) if (t.el.isConnected) present.add(t.region);
      const next = nextRegion(present, registry.regionOf(el), e.shiftKey ? -1 : 1);
      if (next) focusRegion(next);
      return;
    }
    const s = opts.state();
    if (isTyping(el) && e.key !== 'Escape') return;
    if (s.phase === 'idle') {
      if ((e.key === ' ' || e.key === 'Spacebar') && !e.repeat) {
        const held = registry.sourceOf(el)?.held() ?? null;
        if (!held) return;
        e.preventDefault();
        opts.dispatch({ type: 'key-lift', held });
        return;
      }
      if (e.key === 'Enter') {
        const source = registry.sourceOf(el);
        const target = registry.targetOf(el);
        if (pressesItself(el, source?.el ?? target?.el)) return;
        const subject = source?.held() ?? target?.target() ?? null;
        if (!subject) return;
        e.preventDefault();
        opts.dispatch({ type: 'key-ring', subject });
        return;
      }
      if (!inGrid(el)) {
        const region = registry.regionOf(el);
        if (!region) return;
        const els = peersOf(region, el);
        // The element itself, else the nearest registered thing around it (not an outer zone that holds it).
        let here = els.indexOf(el as HTMLElement);
        if (here < 0) here = els.map((x, i) => [x, i] as const).filter(([x]) => x.contains(el)).pop()?.[1] ?? -1;
        const to = rove(els.length, Math.max(0, here), e.key);
        if (to === null) return;
        e.preventDefault();
        settle(region, els[to]!);
        els[to]!.focus();
      }
      return;
    }
    const what = carryKey(s.phase, e.key, e.shiftKey, inGrid(el));
    if (what === null) return;
    e.preventDefault();
    if (what === 'grid') return; // handled above
    if (what === 'explain') {
      // E instead of Enter: the door judges what is in focus; over a target with nothing to explain, nothing is sent.
      opts.dispatch({ type: 'key-explain' });
      return;
    }
    if (what === 'soft' || what === 'queue') {
      mod(what);
      return;
    }
    opts.dispatch(what);
  };

  // Space on a button clicks it on keyup; a lift must not also click.
  const onKeyUp = (e: KeyboardEvent): void => {
    if ((e.key === ' ' || e.key === 'Spacebar') && registry.sourceOf(active())) e.preventDefault();
  };

  root.addEventListener('keydown', onKey);
  root.addEventListener('keyup', onKeyUp);
  root.addEventListener('focusin', onFocus);
  offs.push(() => root.removeEventListener('keydown', onKey), () => root.removeEventListener('keyup', onKeyUp), () => root.removeEventListener('focusin', onFocus));

  return {
    adopt(el, region) {
      const place = (): void => {
        if (inGrid(el)) return;
        const stop = peersOf(region, el).some((x) => x !== el && x.tabIndex === 0);
        el.tabIndex = stop ? -1 : 0;
      };
      // Registered before it is mounted: whether it sits in a grid, and in which part of the screen,
      // is known only once it is in the page (a grid's cells keep the tab stop their grid gave them).
      if (el.isConnected === false && typeof queueMicrotask === 'function') queueMicrotask(() => { if (el.isConnected) place(); });
      else place();
    },
    focusRegion,
    unbind() {
      for (const off of offs.splice(0)) off();
    },
  };
};
